import React, { useState, useEffect, useRef, useContext } from "react";
import {
    Box,
    Typography,
    TextField,
    Button,
    Alert,
    Chip,
    CircularProgress,
    Fade,
} from "@mui/material";
import VerifiedIcon from "@mui/icons-material/Verified";
import EmailIcon from "@mui/icons-material/Email";
import SendIcon from "@mui/icons-material/Send";
import LockClockIcon from "@mui/icons-material/LockClock";
import EditIcon from "@mui/icons-material/Edit";
import clienteAxios from "../../config/Axios";
import AuthContext from "../../Context/auth/AuthContext";

const EmailVerification = ({ onVerified }) => {
    const { usuario } = useContext(AuthContext);

    // Determine initial state based on user data
    const alreadyVerified = !!(usuario?.email_verified_at);
    const hasEmail = !!(usuario?.email);
    const verifiedRef = useRef(alreadyVerified);

    const [email, setEmail] = useState(usuario?.email || "");
    const [code, setCode] = useState("");
    const [step, setStep] = useState(alreadyVerified ? "verified" : (hasEmail ? "has_email" : "idle"));
    const [message, setMessage] = useState(null);
    const [error, setError] = useState(null);
    const [cooldown, setCooldown] = useState(0);
    const [wantsToChange, setWantsToChange] = useState(false);
    const timerRef = useRef(null);

    // Check verification status on mount (only if not already verified)
    useEffect(() => {
        if (!verifiedRef.current) {
            checkStatus();
        } else {
            if (onVerified) onVerified(true);
        }
        return () => {
            if (timerRef.current) clearInterval(timerRef.current);
        };
        // eslint-disable-next-line
    }, []);

    const checkStatus = async () => {
        try {
            const res = await clienteAxios.get("/distribucion/email/status");
            if (res.data.verified) {
                verifiedRef.current = true;
                setStep("verified");
                setEmail(res.data.email);
                if (onVerified) onVerified(true);
            } else if (res.data.email) {
                setEmail(res.data.email);
                setStep("has_email");
            }
        } catch (err) {
            // No verified email, handle silently
        }
    };

    const startCooldown = (seconds = 60) => {
        setCooldown(seconds);
        if (timerRef.current) clearInterval(timerRef.current);
        timerRef.current = setInterval(() => {
            setCooldown((prev) => {
                if (prev <= 1) {
                    clearInterval(timerRef.current);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    };

    const sendCode = async () => {
        if (!email || !email.includes("@")) {
            setError("Ingresa un correo electrónico válido");
            return;
        }
        setError(null);
        setMessage(null);
        setStep("sending");

        try {
            const res = await clienteAxios.post("/distribucion/email/send-code", {
                email,
            });
            setMessage(res.data.message);
            setStep("code_sent");
            startCooldown(60);
        } catch (err) {
            setError(
                err.response?.data?.message || "Error al enviar el código. Intenta de nuevo."
            );
            setStep("idle");
        }
    };

    const verifyCode = async () => {
        if (!code || code.length !== 6) {
            setError("Ingresa el código de 6 dígitos");
            return;
        }
        setError(null);
        setMessage(null);
        setStep("verifying");

        try {
            const res = await clienteAxios.post("/distribucion/email/verify-code", {
                code,
            });
            verifiedRef.current = true;
            setMessage(res.data.message);
            setStep("verified");
            if (onVerified) onVerified(true);
        } catch (err) {
            setError(
                err.response?.data?.message || "Código incorrecto. Intenta de nuevo."
            );
            setStep("code_sent");
        }
    };

    // If already verified, render NOTHING (hide component entirely)
    if (step === "verified") {
        // Silently notify parent and render nothing
        return null;
    }

    return (
        <Fade in>
            <Box
                sx={{
                    background: "linear-gradient(135deg, #FFF0F5 0%, #FCE4EC 100%)",
                    borderRadius: 3,
                    p: 2.5,
                    border: "1px solid #F3CDD4",
                }}
            >
                {/* Header */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                    <EmailIcon sx={{ color: "#801F56", fontSize: 22 }} />
                    <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 700, color: "#801F56" }}
                    >
                        Verificar correo electrónico
                    </Typography>
                    <Chip
                        label="Requerido para pago con tarjeta"
                        size="small"
                        sx={{
                            ml: "auto",
                            background: "#801F56",
                            color: "#fff",
                            fontWeight: 600,
                            fontSize: "0.65rem",
                            height: 22,
                        }}
                    />
                </Box>

                <Typography
                    variant="caption"
                    sx={{ color: "#6B6B6B", display: "block", mb: 2, lineHeight: 1.4 }}
                >
                    Para procesar pagos con tarjeta, necesitamos verificar tu correo electrónico.
                    Te enviaremos un código de 6 dígitos.
                </Typography>

                {/* State: has_email — user has email but not verified */}
                {step === "has_email" && !wantsToChange && (
                    <Box sx={{ mb: 1.5 }}>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                background: "#fff",
                                borderRadius: 2,
                                p: 1.5,
                                border: "1px solid #E0E0E0",
                                mb: 1,
                            }}
                        >
                            <EmailIcon sx={{ color: "#801F56", fontSize: 20 }} />
                            <Box sx={{ flex: 1 }}>
                                <Typography variant="caption" sx={{ color: "#9e9e9e", display: "block" }}>
                                    Tu correo registrado
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: "#333" }}>
                                    {email}
                                </Typography>
                            </Box>
                            <Button
                                size="small"
                                startIcon={<EditIcon sx={{ fontSize: 14 }} />}
                                onClick={() => setWantsToChange(true)}
                                sx={{
                                    textTransform: "none",
                                    color: "#801F56",
                                    fontSize: "0.75rem",
                                    fontWeight: 600,
                                }}
                            >
                                Cambiar
                            </Button>
                        </Box>
                        <Button
                            variant="contained"
                            onClick={sendCode}
                            fullWidth
                            startIcon={<SendIcon />}
                            sx={{
                                background: "linear-gradient(135deg, #801F56 0%, #D87390 100%)",
                                "&:hover": {
                                    background: "linear-gradient(135deg, #6B1A48 0%, #A93B7A 100%)",
                                },
                                borderRadius: 2,
                                textTransform: "none",
                                fontWeight: 600,
                                fontSize: "0.85rem",
                                py: 1,
                            }}
                        >
                            Enviar código de verificación
                        </Button>
                    </Box>
                )}

                {/* State: has_email + wants to change, or idle (no email) */}
                {((step === "has_email" && wantsToChange) || step === "idle" || step === "sending") && (
                    <Box sx={{ display: "flex", gap: 1, mb: 1.5 }}>
                        <TextField
                            size="small"
                            placeholder="tu@correo.com"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            fullWidth
                            sx={{
                                "& .MuiOutlinedInput-root": {
                                    borderRadius: 2,
                                    background: "#fff",
                                    fontSize: "0.9rem",
                                    "&.Mui-focused fieldset": {
                                        borderColor: "#E46EA7",
                                    },
                                },
                            }}
                        />
                        <Button
                            variant="contained"
                            onClick={sendCode}
                            disabled={step === "sending" || !email}
                            startIcon={
                                step === "sending" ? (
                                    <CircularProgress size={16} sx={{ color: "#fff" }} />
                                ) : (
                                    <SendIcon />
                                )
                            }
                            sx={{
                                background:
                                    "linear-gradient(135deg, #801F56 0%, #D87390 100%)",
                                "&:hover": {
                                    background:
                                        "linear-gradient(135deg, #6B1A48 0%, #A93B7A 100%)",
                                },
                                borderRadius: 2,
                                textTransform: "none",
                                fontWeight: 600,
                                minWidth: 140,
                                fontSize: "0.85rem",
                                whiteSpace: "nowrap",
                            }}
                        >
                            {step === "sending" ? "Enviando..." : "Enviar código"}
                        </Button>
                    </Box>
                )}

                {/* Code Input */}
                {(step === "code_sent" || step === "verifying") && (
                    <>
                        <Typography
                            variant="caption"
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                                color: "#801F56",
                                fontWeight: 600,
                                mb: 1,
                            }}
                        >
                            <LockClockIcon sx={{ fontSize: 16 }} />
                            Código enviado a {email}
                        </Typography>

                        <Box sx={{ display: "flex", gap: 1, mb: 1.5 }}>
                            <TextField
                                size="small"
                                placeholder="000000"
                                value={code}
                                onChange={(e) => {
                                    const val = e.target.value.replace(/[^0-9]/g, "").slice(0, 6);
                                    setCode(val);
                                }}
                                inputProps={{
                                    maxLength: 6,
                                    style: {
                                        textAlign: "center",
                                        letterSpacing: "8px",
                                        fontWeight: 700,
                                        fontSize: "1.2rem",
                                        fontFamily: "monospace",
                                    },
                                }}
                                fullWidth
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: 2,
                                        background: "#fff",
                                        "&.Mui-focused fieldset": {
                                            borderColor: "#E46EA7",
                                        },
                                    },
                                }}
                            />
                            <Button
                                variant="contained"
                                onClick={verifyCode}
                                disabled={step === "verifying" || code.length !== 6}
                                startIcon={
                                    step === "verifying" ? (
                                        <CircularProgress size={16} sx={{ color: "#fff" }} />
                                    ) : (
                                        <VerifiedIcon />
                                    )
                                }
                                sx={{
                                    background:
                                        "linear-gradient(135deg, #801F56 0%, #E46EA7 100%)",
                                    "&:hover": {
                                        background:
                                            "linear-gradient(135deg, #6B1A48 0%, #D87390 100%)",
                                    },
                                    borderRadius: 2,
                                    textTransform: "none",
                                    fontWeight: 600,
                                    minWidth: 130,
                                    fontSize: "0.85rem",
                                    whiteSpace: "nowrap",
                                }}
                            >
                                {step === "verifying" ? "Verificando..." : "Verificar"}
                            </Button>
                        </Box>

                        {/* Resend */}
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <Button
                                size="small"
                                onClick={sendCode}
                                disabled={cooldown > 0}
                                sx={{
                                    textTransform: "none",
                                    color: cooldown > 0 ? "#9e9e9e" : "#801F56",
                                    fontWeight: 600,
                                    fontSize: "0.75rem",
                                }}
                            >
                                {cooldown > 0
                                    ? `Reenviar en ${cooldown}s`
                                    : "Reenviar código"}
                            </Button>
                            <Button
                                size="small"
                                onClick={() => {
                                    setStep("idle");
                                    setCode("");
                                    setError(null);
                                    setMessage(null);
                                    setWantsToChange(false);
                                }}
                                sx={{
                                    textTransform: "none",
                                    color: "#6B6B6B",
                                    fontSize: "0.75rem",
                                }}
                            >
                                Cambiar correo
                            </Button>
                        </Box>
                    </>
                )}

                {/* Messages */}
                {error && (
                    <Alert
                        severity="error"
                        sx={{ mt: 1, borderRadius: 2, fontSize: "0.8rem" }}
                        onClose={() => setError(null)}
                    >
                        {error}
                    </Alert>
                )}
                {message && !error && (
                    <Alert
                        severity="success"
                        sx={{ mt: 1, borderRadius: 2, fontSize: "0.8rem" }}
                        onClose={() => setMessage(null)}
                    >
                        {message}
                    </Alert>
                )}
            </Box>
        </Fade>
    );
};

export default EmailVerification;
